export {};

import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import { KeyRound, Printer, RotateCcw, X } from "lucide-react";
import { useState } from "react";
import QRCode from "qrcode";
import { QrCode } from "./qr";

type PrintEntry = {
  accountId: string;
  studentId: string;
  studentCode: string;
  studentName: string;
  href: string;
  url: string;
};

type PrintResponse = { entries: PrintEntry[]; persistent: boolean };
type Passwords = Record<string, string>;
type ResetResponse = { accounts?: Array<{ studentCode: string; password: string }> ; error?: string };

let previewRoot: Root | null = null;
let previewHost: HTMLDivElement | null = null;
let loading = false;

async function requestPrintQr(classId: string, regenerate = false) {
  const response = await fetch("/api/student/print-qr/class", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ classId, regenerate }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error ?? "Impossible de générer les QR codes");
  return body as PrintResponse;
}

function chunk<T>(items: T[], size: number) {
  const result: T[][] = [];
  for (let index = 0; index < items.length; index += size) result.push(items.slice(index, index + size));
  return result;
}


function htmlEscape(value: string) {
  return value.replace(/[&<>"']/g, (char) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  })[char]!);
}

/**
 * Impression dans un document indépendant de l'application : évite les
 * feuilles blanches causées par les styles du tableau de bord et les SVG
 * mal rendus dans l'aperçu d'impression de certains navigateurs.
 * La fenêtre est ouverte immédiatement dans le geste utilisateur.
 */
async function printCards(entries: PrintEntry[], className: string, passwords: Passwords) {
  const popup = window.open("", "_blank");
  if (!popup) {
    window.alert("L'impression est bloquée par le navigateur. Autorise les fenêtres pop-up pour CompetenceO.");
    return;
  }

  popup.document.write("<!doctype html><html lang='fr'><head><meta charset='utf-8'><title>Préparation des QR codes</title></head><body><p>Préparation des fiches élèves…</p></body></html>");
  popup.document.close();
  try {
    const images = await Promise.all(entries.map((entry) => QRCode.toDataURL(entry.url, {
      width: 560, margin: 3, errorCorrectionLevel: "M",
      color: { dark: "#000000", light: "#ffffff" },
    })));
    if (popup.closed) return;
    const pages = chunk(entries.map((entry, index) => ({ ...entry, image: images[index] })), 8);
    const html = pages.map((page) => `<section class="sheet">${page.map((entry) => {
      const password = passwords[entry.studentCode];
      return `<article class="card">
        <div class="class-name">${htmlEscape(className)}</div>
        <img src="${entry.image}" width="176" height="176" alt="QR code élève">
        <strong class="student-name">${htmlEscape(entry.studentName)}</strong>
        <div class="credential"><span>Identifiant :</span> <b>${htmlEscape(entry.studentCode || "—")}</b></div>
        ${password ? `<div class="credential"><span>Mot de passe :</span> <b>${htmlEscape(password)}</b></div>` : ""}
      </article>`;
    }).join("")}</section>`).join("");

    popup.document.open();
    popup.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8">
      <meta name="viewport" content="width=device-width,initial-scale=1">
      <title>CompetenceO — QR codes — ${htmlEscape(className)}</title>
      <style>
        @page { size: A4 portrait; margin: 0; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; background: #fff; color: #111; font-family: Arial, sans-serif; }
        .sheet { width: 210mm; height: 297mm; padding: 10mm; display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          grid-template-rows: repeat(4, minmax(0, 1fr)); gap: 5mm;
          break-after: page; page-break-after: always; }
        .sheet:last-child { break-after: auto; page-break-after: auto; }
        .card { border: 1px dashed #aab5c4; border-radius: 2mm; min-width: 0; min-height: 0;
          padding: 2.5mm; display: flex; flex-direction: column; align-items: center;
          justify-content: center; gap: 1mm; overflow: hidden;
          break-inside: avoid; page-break-inside: avoid; }
        .class-name { font-weight: 800; background: #ffeb3b; color: #111;
          padding: 1mm 3mm; font-size: 12pt; border-radius: 1mm;
          -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .card img { display: block; width: 39mm; height: 39mm; object-fit: contain; flex-shrink: 0; }
        .student-name { font-size: 12pt; text-align: center; line-height: 1.1; }
        .credential { font-size: 9.5pt; line-height: 1.15; text-align: center; overflow-wrap: anywhere; }
        .credential span { color: #333; }
        .credential b { font-family: Arial, sans-serif; font-weight: 800; }
        @media screen { body { background: #e8edf3; }
          .sheet { margin: 15px auto; background: white; box-shadow: 0 2px 15px #b7c1ce; } }
        @media print { .sheet { margin: 0 !important; box-shadow: none !important; } }
      </style></head><body>${html}</body></html>`);
    popup.document.close();
    await Promise.all(Array.from(popup.document.images, (img) =>
      typeof img.decode === "function" ? img.decode() : Promise.resolve()));
    await popup.document.fonts?.ready;
    if (!popup.closed) {
      popup.focus();
      popup.print();
    }
  } catch (error) {
    console.error("Impression des QR codes", error);
    if (!popup.closed) popup.close();
    window.alert("Impossible de préparer les QR codes pour l'impression. Réessaie.");
  }
}

function closePreview() {
  previewRoot?.unmount();
  previewRoot = null;
  previewHost?.remove();
  previewHost = null;
}

function PrintPreview({ classId, className, entries, passwords }: { classId: string; className: string; entries: PrintEntry[]; passwords: Passwords }) {
  const pages = chunk(entries, 8);
  const [resettingPasswords, setResettingPasswords] = useState(false);

  async function regenerate() {
    if (!window.confirm("Régénérer les QR codes de cette classe ?\n\nLes feuilles déjà imprimées pour ces élèves ne fonctionneront plus.")) return;
    try {
      const result = await requestPrintQr(classId, true);
      renderPreview(classId, className, result.entries, passwords);
    } catch (reason) {
      window.alert(reason instanceof Error ? reason.message : "Régénération impossible");
    }
  }


  async function resetAndPrintPasswords() {
    if (resettingPasswords) return;
    if (!window.confirm(`Générer de nouveaux mots de passe pour les ${entries.length} élèves actifs de « ${className} » ?\n\nATTENTION : les anciens mots de passe cesseront de fonctionner et les sessions élèves seront déconnectées. Les nouveaux mots de passe ne pourront être récupérés que sur cette feuille, avant de la fermer.\n\nLes QR codes restent utilisables.`)) return;
    setResettingPasswords(true);
    try {
      const response = await fetch("/api/teacher/students/reset-all", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ classId, activeOnly: true }),
      });
      const body = await response.json().catch(() => ({})) as ResetResponse;
      if (!response.ok) throw new Error(body.error ?? "Réinitialisation impossible");
      const newPasswords: Passwords = {};
      for (const account of body.accounts ?? []) newPasswords[account.studentCode] = account.password;
      const fresh = await requestPrintQr(classId);
      if (fresh.entries.some((entry) => !newPasswords[entry.studentCode])) {
        throw new Error("Les mots de passe ont été réinitialisés, mais certaines fiches n'ont pas été associées. Utilise la gestion des comptes pour réinitialiser les comptes concernés.");
      }
      renderPreview(classId, className, fresh.entries, newPasswords);
    } catch (reason) {
      window.alert(reason instanceof Error ? reason.message : "Réinitialisation impossible");
    } finally {
      setResettingPasswords(false);
    }
  }

  return <div className="student-qr-print-overlay">
    <div className="student-qr-print-toolbar">
      <div>
        <strong>QR codes · {className}</strong>
        <span>{entries.length} élève{entries.length > 1 ? "s" : ""} · {pages.length} page{pages.length > 1 ? "s" : ""} A4 · 8 maximum par page</span>
      </div>
      <button className="secondary" onClick={() => void regenerate()} title="Invalide les anciennes cartes et crée de nouveaux QR"><RotateCcw size={17} /> Régénérer</button>

      <button className="secondary" disabled={resettingPasswords} onClick={() => void resetAndPrintPasswords()} title="Réinitialise les mots de passe des élèves actifs avant de les imprimer">
        <KeyRound size={17} /> {resettingPasswords ? "Réinitialisation…" : "Nouveaux mots de passe"}
      </button>
      <button className="primary" disabled={resettingPasswords} onClick={() => void printCards(entries, className, passwords)}><Printer size={17} /> Imprimer</button>
      <button className="secondary icon-only" onClick={closePreview} aria-label="Fermer"><X size={19} /></button>
    </div>

    <div className="student-qr-print-pages">
      {pages.map((page, pageIndex) => <section className="student-qr-print-sheet" key={pageIndex} aria-label={`Page ${pageIndex + 1}`}>
        {page.map((entry) => <article className="student-qr-print-card" key={entry.accountId}>
          <div className="student-qr-print-class"><span>{className}</span></div>
          <div className="student-qr-print-code"><QrCode value={entry.url} size={190} title={`QR code de ${entry.studentName}`} /></div>
          <div className="student-qr-print-name">{entry.studentName}</div>
          <div className="student-qr-print-identifier">Identifiant : <strong>{entry.studentCode}</strong></div>
          {passwords[entry.studentCode] && <div className="student-qr-print-password">Mot de passe : <strong>{passwords[entry.studentCode]}</strong></div>}
        </article>)}
      </section>)}
    </div>
  </div>;
}

function renderPreview(classId: string, className: string, entries: PrintEntry[], passwords: Passwords = {}) {
  if (!previewHost) {
    previewHost = document.createElement("div");
    previewHost.className = "student-qr-print-root";
    document.body.appendChild(previewHost);
    previewRoot = createRoot(previewHost);
  }
  previewRoot!.render(<PrintPreview classId={classId} className={className} entries={entries} passwords={passwords} />);
}

async function openPrintPreview(picker: HTMLSelectElement, button: HTMLButtonElement) {
  if (loading) return;
  const option = picker.options[picker.selectedIndex];
  if (!option?.value) return;
  loading = true;
  button.disabled = true;
  const previous = button.innerHTML;
  button.textContent = "Génération des QR…";
  try {
    const result = await requestPrintQr(option.value);
    if (!result.entries.length) {
      window.alert("Aucun compte élève actif dans cette classe. Crée d’abord les comptes élèves.");
      return;
    }
    renderPreview(option.value, option.textContent?.trim() || "Classe", result.entries);
  } catch (reason) {
    window.alert(reason instanceof Error ? reason.message : "Impossible de générer les QR codes");
  } finally {
    loading = false;
    button.disabled = false;
    button.innerHTML = previous;
  }
}

function installPrintButton() {
  const page = document.querySelector<HTMLElement>(".accounts-page");
  if (!page) {
    closePreview();
    return;
  }
  if (page.querySelector("[data-print-student-qr]")) return;
  const picker = page.querySelector<HTMLSelectElement>(".account-class-picker select");
  const actions = page.querySelector<HTMLElement>(".management-heading > div:last-child");
  if (!picker || !actions) return;

  const button = document.createElement("button");
  button.type = "button";
  button.className = "secondary";
  button.dataset.printStudentQr = "true";
  button.innerHTML = "<span aria-hidden='true'>🖨️</span> Imprimer les QR codes";
  button.addEventListener("click", () => void openPrintPreview(picker, button));
  actions.prepend(button);
}

const printButtonObserver = new MutationObserver(installPrintButton);
printButtonObserver.observe(document.documentElement, { childList: true, subtree: true });
window.setTimeout(installPrintButton, 0);
